import { getPageKey } from './pageKey';

/**
 * 공통 컨텍스트 빌드 (모든 이벤트에 자동 주입)
 * @param {object} additionalParams - 추가 파라미터 (우선 override)
 * @param {object} authStateOverride - Recoil authState를 직접 전달 (선택, localStorage보다 우선)
 * @returns {object} 공통 컨텍스트 + 추가 파라미터
 */
export const buildContext = (additionalParams = {}, authStateOverride = null) => {
  const context = {};

  // 우선순위: authStateOverride > localStorage > 없음
  let user = null;
  let isLoggedIn = false;

  // 1순위: authStateOverride (Recoil state 직접 전달)
  if (authStateOverride?.isLoggedIn && authStateOverride?.user) {
    user = authStateOverride.user;
    isLoggedIn = true;
  } else {
    // 2순위: localStorage에서 읽기 (recoil-persist로 저장된 데이터)
    try {
      const authStateStr = localStorage.getItem('recoil-persist');
      if (authStateStr) {
        try {
          const persistedState = JSON.parse(authStateStr);
          // recoil-persist의 저장 구조: { "authState": { isLoggedIn, user, token } }
          const authState = persistedState?.authState;
          
          if (authState?.isLoggedIn && authState?.user) {
            user = authState.user;
            isLoggedIn = true;
          }
        } catch (parseError) {
          // JSON 파싱 실패 시 조용히 처리
          if (process.env.NODE_ENV === 'development') {
            console.warn('[Analytics] Failed to parse recoil-persist:', parseError);
          }
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Analytics] Failed to read auth state from localStorage:', error);
      }
    }
  }

  // 사용자 정보가 있으면 공통 파라미터 설정
  if (isLoggedIn && user) {
    const isMaster = Number(user?.club_admin_level) === 10;
    
    // 공통 파라미터 설정 (null이 되지 않도록 명시적 변환)
    // gc_id는 항상 문자열로 변환 (BigQuery에서 string_value로 저장되도록 강제)
    const gcIdValue = user?.club_id;
    context.gc_id = gcIdValue != null ? String(gcIdValue) : null;
    
    // gc_name도 항상 문자열로 보장 (null이 아닌 경우만)
    const gcNameValue = isMaster ? 'CEO' : (user?.club_name || user?.name);
    context.gc_name = gcNameValue != null ? String(gcNameValue) : null;
    
    context.user_role = user?.club_admin_level ? String(user.club_admin_level) : null;
    context.is_master = isMaster;
    
    // 디버그 모드에서 확인
    if (process.env.NODE_ENV === 'development' && window.__ANALYTICS_DEBUG__) {
      console.log('[Analytics] buildContext - User data:', {
        gc_id: context.gc_id,
        gc_name: context.gc_name,
        user_role: context.user_role,
        is_master: context.is_master,
        source: authStateOverride ? 'Recoil state' : 'localStorage',
      });
    }
  } else {
    // 로그인하지 않은 상태 - gc_id는 null이지만 명시적으로 설정
    context.gc_id = null;
    context.gc_name = null;
    context.user_role = null;
    context.is_master = false;
    
    if (process.env.NODE_ENV === 'development' && window.__ANALYTICS_DEBUG__) {
      console.log('[Analytics] buildContext - Not logged in or no user data');
    }
  }

  // 페이지 정보 (항상 설정, null이 되지 않도록)
  if (typeof window !== 'undefined' && window.location) {
    const pathname = window.location.pathname || '';
    const pageKey = getPageKey(pathname);
    context.page_path = pathname || '';
    context.page_key = pageKey || 'unknown';
    context.page_location = window.location.href || '';
    
    // 디버그 모드에서 확인
    if (process.env.NODE_ENV === 'development' && window.__ANALYTICS_DEBUG__) {
      console.log('[Analytics] buildContext - Page data:', {
        page_path: context.page_path,
        page_key: context.page_key,
        page_location: context.page_location,
      });
    }
  } else {
    // window가 없거나 location이 없는 경우 기본값
    context.page_path = '';
    context.page_key = 'unknown';
    context.page_location = '';
  }

  // 세션 태그
  context.session_tag = 'admin_center';

  // 추가 파라미터가 우선 (override)
  return { ...context, ...additionalParams };
};
